import { TaskExtended } from '../../../types';

// Simple data structure - array of groups, ordered by recency
type TaskGroup = TaskExtended[];

/**
 * Groups tasks by tagName and creation time, then filters to show only the current task's group.
 *
 * Algorithm:
 * 1. Sort tasks by createdAt descending (newest first)
 * 2. Build groups where each group contains tasks with unique tagNames
 * 3. Find which group contains the current task
 * 4. Return only tasks from that group
 * 5. Determine if warning should show (current task not in most recent group)
 *
 * @param tasks - Array of tasks to group and filter
 * @param currentTaskId - The ID of the currently viewed task
 * @returns Filtered tasks, warning flag, and newest task with same tagName (if applicable)
 */
export function groupAndFilterTasks(
  tasks: TaskExtended[],
  currentTaskId: number
): {
  filteredTasks: TaskExtended[];
  showWarning: boolean;
  newestTask: TaskExtended | null;
} {
  // Early return: if no duplicate tagNames, all tasks are in one group (newest)
  const tagNames = tasks.map((t) => t.tagName);
  const uniqueTagNames = new Set(tagNames);
  if (tagNames.length === uniqueTagNames.size) {
    return {
      filteredTasks: tasks,
      showWarning: false,
      newestTask: null,
    };
  }

  // Step 1: Sort by createdAt descending (newest first)
  const sortedTasks = [...tasks].sort(
    (a, b) => new Date(b.taskCreatedAt).getTime() - new Date(a.taskCreatedAt).getTime()
  );

  // Step 2: Group tasks - each group contains tasks with unique tagNames
  const groups: TaskGroup[] = [];

  for (const task of sortedTasks) {
    let addedToGroup = false;

    // Try to add to existing groups (check if tagName already exists)
    for (const group of groups) {
      const tagNameExists = group.some((t) => t.tagName === task.tagName);
      if (!tagNameExists) {
        group.push(task);
        addedToGroup = true;
        break;
      }
    }

    // If tagName exists in all groups, create new group
    if (!addedToGroup) {
      groups.push([task]);
    }
  }

  // Step 3: Find which group contains current task
  const currentGroupIndex = groups.findIndex((group) =>
    group.some((t) => t.actionTaskId === currentTaskId)
  );

  // Step 4: Return filtered tasks (only current group)
  const filteredTasks = currentGroupIndex >= 0 ? groups[currentGroupIndex] : tasks;

  // Step 5: Determine if warning should show (not in Group 1 = index 0)
  const showWarning = currentGroupIndex > 0;

  // Step 6: Find newest task with same tagName for banner link
  let newestTask: TaskExtended | null = null;
  if (showWarning && groups[0]) {
    const currentTask = tasks.find((t) => t.actionTaskId === currentTaskId);
    if (currentTask) {
      newestTask = groups[0].find((t) => t.tagName === currentTask.tagName) || null;
    }
  }

  return { filteredTasks, showWarning, newestTask };
}
